import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getAllPosts, getPost, savePost, deletePost, slugify } from '../services/blogService';
import { serverTimestamp } from 'firebase/firestore';
import './AdminBlogPage.css';

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEditing = !!slug;

  // List view state
  const [posts, setPosts] = useState(null);

  // Editor state
  const [title, setTitle] = useState('');
  const [postSlug, setPostSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');
  const [published, setPublished] = useState(false);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Load list
  useEffect(() => {
    if (!isEditing) {
      getAllPosts().then(setPosts).catch(() => setPosts([]));
    }
  }, [isEditing]);

  // Load post for editing
  useEffect(() => {
    if (isEditing) {
      if (slug === 'new') return; // blank editor
      getPost(slug).then((p) => {
        if (!p) { navigate('/admin/blog'); return; }
        setTitle(p.title || '');
        setPostSlug(p.id);
        setExcerpt(p.excerpt || '');
        setBody(p.body || '');
        setPublished(!!p.published);
      });
    }
  }, [slug, isEditing, navigate]);

  // Auto-generate slug from title when creating new
  useEffect(() => {
    if (slug === 'new') setPostSlug(slugify(title));
  }, [title, slug]);

  async function handleSave(publishNow) {
    if (!title.trim() || !postSlug.trim()) {
      setError('Title and slug are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const isPublishing = publishNow !== undefined ? publishNow : published;
      await savePost(postSlug, {
        title: title.trim(),
        excerpt: excerpt.trim(),
        body,
        published: isPublishing,
        publishedAt: isPublishing ? serverTimestamp() : null,
      });
      setPublished(isPublishing);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (slug === 'new') navigate(`/admin/blog/${postSlug}`, { replace: true });
    } catch (e) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deletePost(postSlug);
      navigate('/admin/blog');
    } catch {
      setError('Failed to delete post.');
      setDeleting(false);
    }
  }

  // ── List view ──
  if (!isEditing) {
    return (
      <div className="ablog-page">
        <header className="ablog-header">
          <button className="ablog-back" onClick={() => navigate('/admin')}>← Admin</button>
          <span className="ablog-header-title">Blog Posts</span>
          <button className="ablog-btn-new" onClick={() => navigate('/admin/blog/new')}>+ New Post</button>
        </header>

        <div className="ablog-body">
          {posts === null && <div className="ablog-loading">Loading…</div>}
          {posts && posts.length === 0 && (
            <div className="ablog-empty">No posts yet. Create your first one.</div>
          )}
          {posts && posts.map((p) => (
            <div key={p.id} className="ablog-row" onClick={() => navigate(`/admin/blog/${p.id}`)}>
              <div className="ablog-row-main">
                <span className="ablog-row-title">{p.title}</span>
                <span className={`ablog-badge ${p.published ? 'ablog-badge--live' : 'ablog-badge--draft'}`}>
                  {p.published ? 'Live' : 'Draft'}
                </span>
              </div>
              <div className="ablog-row-meta">{formatDate(p.publishedAt || p.createdAt)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Editor view ──
  return (
    <div className="ablog-page">
      <header className="ablog-header">
        <button className="ablog-back" onClick={() => navigate('/admin/blog')}>← Posts</button>
        <span className="ablog-header-title">{slug === 'new' ? 'New Post' : 'Edit Post'}</span>
        <div className="ablog-header-actions">
          <button className="ablog-btn-preview" onClick={() => setPreview((v) => !v)}>
            {preview ? 'Edit' : 'Preview'}
          </button>
          {slug !== 'new' && (
            <button className="ablog-btn-delete" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </header>

      <div className="ablog-editor">
        {error && <div className="ablog-error">{error}</div>}

        {!preview ? (
          <>
            <div className="ablog-field">
              <label className="ablog-label">Title</label>
              <input
                className="ablog-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title"
              />
            </div>

            <div className="ablog-field">
              <label className="ablog-label">Slug (URL)</label>
              <input
                className="ablog-input ablog-input--mono"
                value={postSlug}
                onChange={(e) => setPostSlug(e.target.value)}
                placeholder="post-url-slug"
                disabled={slug !== 'new'}
              />
              {postSlug && <span className="ablog-slug-preview">/blog/{postSlug}</span>}
            </div>

            <div className="ablog-field">
              <label className="ablog-label">Excerpt <span className="ablog-label-hint">(shown on blog list)</span></label>
              <input
                className="ablog-input"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="One or two sentence summary…"
              />
            </div>

            <div className="ablog-field ablog-field--body">
              <label className="ablog-label">Body <span className="ablog-label-hint">(Markdown supported)</span></label>
              <textarea
                className="ablog-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your post here…&#10;&#10;**Bold**, *italic*, ## Headings, - Lists"
              />
            </div>
          </>
        ) : (
          <div className="ablog-preview">
            <h1 className="ablog-preview-title display-font">{title || 'Untitled'}</h1>
            <div className="ablog-preview-body">
              <ReactMarkdown>{body || '*Nothing to preview yet.*'}</ReactMarkdown>
            </div>
          </div>
        )}

        <div className="ablog-actions">
          <label className="ablog-publish-toggle">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
            />
            Publish
          </label>
          <div className="ablog-save-group">
            {saved && <span className="ablog-saved">✓ Saved</span>}
            <button
              className="ablog-btn-save"
              onClick={() => handleSave()}
              disabled={saving}
            >
              {saving ? 'Saving…' : published ? 'Save & Publish' : 'Save Draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
